"use client";

import { useCallback, useMemo } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import type { SortOption, ActiveFilter } from "./filter-bar";
import { buildActiveFilters } from "./filter-utils";

interface UseProductFiltersOptions {
	/** Categories resolved from URL slugs (server-side) for active filter display */
	resolvedCategories?: Array<{ slug: string; id: string; name: string }>;
	/** Whether to include category filter (only for /products page) */
	enableCategoryFilter?: boolean;
}

interface UseProductFiltersResult {
	// Selected filter values (parsed from the URL — the single source of truth)
	selectedCategories: string[];
	selectedColors: string[];
	selectedSizes: string[];
	selectedPriceRange: string | null;
	searchQuery: string;
	selectedPeptideCategories: string[];
	selectedResearchLevel: string;
	selectedAvailability: string;
	sortValue: SortOption;

	// Active filters for display
	activeFilters: ActiveFilter[];

	// Handlers
	handleCategoryToggle: (slug: string) => void;
	handleColorToggle: (color: string) => void;
	handleSizeToggle: (size: string) => void;
	handlePriceRangeChange: (range: string | null) => void;
	handleSearchChange: (query: string) => void;
	handlePeptideCategoryToggle: (name: string) => void;
	handleResearchLevelChange: (level: string) => void;
	handleAvailabilityChange: (availability: string) => void;
	handleSortChange: (sort: SortOption) => void;
	handleRemoveFilter: (key: string, value: string) => void;
	handleClearFilters: () => void;
}

/**
 * URL filter-state manager for server-filtered product listings.
 *
 * ALL filtering happens on the server (Saleor-native filters + residual
 * filtering on the complete result set — see src/lib/catalog/filtering.ts).
 * This hook only reads/writes the URL params; every filter or sort change
 * resets pagination so the user never lands on a stale page slice.
 */
export function useProductFilters({
	resolvedCategories = [],
	enableCategoryFilter = false,
}: UseProductFiltersOptions = {}): UseProductFiltersResult {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();

	// Parse current filters from URL
	const selectedCategories = useMemo(
		() => (enableCategoryFilter ? searchParams.get("categories")?.split(",").filter(Boolean) || [] : []),
		[searchParams, enableCategoryFilter],
	);
	const selectedColors = useMemo(
		() => searchParams.get("colors")?.split(",").filter(Boolean) || [],
		[searchParams],
	);
	const selectedSizes = useMemo(
		() => searchParams.get("sizes")?.split(",").filter(Boolean) || [],
		[searchParams],
	);
	const selectedPriceRange = searchParams.get("price") || null;
	const searchQuery = searchParams.get("q") || "";
	const selectedPeptideCategories = useMemo(
		() => searchParams.get("peptideCategories")?.split(",").filter(Boolean) || [],
		[searchParams],
	);
	const selectedResearchLevel = searchParams.get("researchLevel") || "all";
	const selectedAvailability = searchParams.get("availability") || "all";
	const sortValue = (searchParams.get("sort") as SortOption) || "featured";

	// Update URL with new filters — triggers a server re-fetch with the new state.
	const updateFilters = useCallback(
		(
			updates: {
				categories?: string[];
				colors?: string[];
				sizes?: string[];
				price?: string | null;
				sort?: string;
				q?: string;
				peptideCategories?: string[];
				researchLevel?: string;
				availability?: string;
			},
			options?: { history?: "push" | "replace" },
		) => {
			const params = new URLSearchParams(searchParams.toString());

			// Any filter/sort change invalidates the current page position —
			// matches on other pages must be reachable from page 1.
			params.delete("page");
			params.delete("cursor");
			params.delete("direction");

			if (updates.categories !== undefined) {
				if (updates.categories.length > 0) {
					params.set("categories", updates.categories.join(","));
				} else {
					params.delete("categories");
				}
			}

			if (updates.colors !== undefined) {
				if (updates.colors.length > 0) {
					params.set("colors", updates.colors.join(","));
				} else {
					params.delete("colors");
				}
			}

			if (updates.sizes !== undefined) {
				if (updates.sizes.length > 0) {
					params.set("sizes", updates.sizes.join(","));
				} else {
					params.delete("sizes");
				}
			}

			if (updates.price !== undefined) {
				if (updates.price) {
					params.set("price", updates.price);
				} else {
					params.delete("price");
				}
			}

			if (updates.sort !== undefined) {
				if (updates.sort && updates.sort !== "featured") {
					params.set("sort", updates.sort);
				} else {
					params.delete("sort");
				}
			}

			if (updates.q !== undefined) {
				if (updates.q.trim()) {
					params.set("q", updates.q.trim());
				} else {
					params.delete("q");
				}
			}

			if (updates.peptideCategories !== undefined) {
				if (updates.peptideCategories.length > 0) {
					params.set("peptideCategories", updates.peptideCategories.join(","));
				} else {
					params.delete("peptideCategories");
				}
			}

			if (updates.researchLevel !== undefined) {
				if (updates.researchLevel && updates.researchLevel !== "all") {
					params.set("researchLevel", updates.researchLevel);
				} else {
					params.delete("researchLevel");
				}
			}

			if (updates.availability !== undefined) {
				if (updates.availability && updates.availability !== "all") {
					params.set("availability", updates.availability);
				} else {
					params.delete("availability");
				}
			}

			const queryString = params.toString();
			const url = queryString ? `${pathname}?${queryString}` : pathname;
			if (options?.history === "replace") {
				router.replace(url, { scroll: false });
			} else {
				router.push(url, { scroll: false });
			}
		},
		[router, pathname, searchParams],
	);

	// Filter handlers
	const handleCategoryToggle = useCallback(
		(slug: string) => {
			const newCategories = selectedCategories.includes(slug)
				? selectedCategories.filter((c) => c !== slug)
				: [...selectedCategories, slug];
			updateFilters({ categories: newCategories });
		},
		[selectedCategories, updateFilters],
	);

	const handleColorToggle = useCallback(
		(color: string) => {
			const newColors = selectedColors.includes(color)
				? selectedColors.filter((c) => c !== color)
				: [...selectedColors, color];
			updateFilters({ colors: newColors });
		},
		[selectedColors, updateFilters],
	);

	const handleSizeToggle = useCallback(
		(size: string) => {
			const newSizes = selectedSizes.includes(size)
				? selectedSizes.filter((s) => s !== size)
				: [...selectedSizes, size];
			updateFilters({ sizes: newSizes });
		},
		[selectedSizes, updateFilters],
	);

	const handlePriceRangeChange = useCallback(
		(range: string | null) => {
			updateFilters({ price: range });
		},
		[updateFilters],
	);

	const handleSearchChange = useCallback(
		(query: string) => {
			// replace, not push — a debounced type-ahead must not spam the history
			// with one entry per committed keystroke batch.
			updateFilters({ q: query }, { history: "replace" });
		},
		[updateFilters],
	);

	const handlePeptideCategoryToggle = useCallback(
		(name: string) => {
			const newCats = selectedPeptideCategories.includes(name)
				? selectedPeptideCategories.filter((c) => c !== name)
				: [...selectedPeptideCategories, name];
			updateFilters({ peptideCategories: newCats });
		},
		[selectedPeptideCategories, updateFilters],
	);

	const handleResearchLevelChange = useCallback(
		(level: string) => {
			updateFilters({ researchLevel: level });
		},
		[updateFilters],
	);

	const handleAvailabilityChange = useCallback(
		(availability: string) => {
			updateFilters({ availability });
		},
		[updateFilters],
	);

	const handleSortChange = useCallback(
		(sort: SortOption) => {
			updateFilters({ sort });
		},
		[updateFilters],
	);

	const handleRemoveFilter = useCallback(
		(key: string, value: string) => {
			if (key === "category") {
				const category = resolvedCategories.find((c) => c.name === value);
				if (category) {
					const newCategories = selectedCategories.filter((c) => c !== category.slug);
					updateFilters({ categories: newCategories });
				}
			} else if (key === "color") {
				const newColors = selectedColors.filter((c) => c !== value);
				updateFilters({ colors: newColors });
			} else if (key === "size") {
				const newSizes = selectedSizes.filter((s) => s !== value);
				updateFilters({ sizes: newSizes });
			} else if (key === "price") {
				updateFilters({ price: null });
			} else if (key === "search") {
				updateFilters({ q: "" });
			} else if (key === "peptideCategory") {
				const newCats = selectedPeptideCategories.filter((c) => c !== value);
				updateFilters({ peptideCategories: newCats });
			} else if (key === "researchLevel") {
				updateFilters({ researchLevel: "all" });
			} else if (key === "availability") {
				updateFilters({ availability: "all" });
			}
		},
		[
			resolvedCategories,
			selectedCategories,
			selectedColors,
			selectedSizes,
			selectedPeptideCategories,
			updateFilters,
		],
	);

	const handleClearFilters = useCallback(() => {
		router.push(pathname, { scroll: false });
	}, [router, pathname]);

	// Build active filters for display
	const activeFilters = useMemo(() => {
		const filters = buildActiveFilters({
			colors: selectedColors,
			sizes: selectedSizes,
			priceRange: selectedPriceRange,
		});

		resolvedCategories.forEach((cat) => {
			filters.unshift({ key: "category", label: "Category", value: cat.name });
		});

		if (searchQuery.trim()) {
			filters.push({ key: "search", label: "Search", value: searchQuery });
		}
		selectedPeptideCategories.forEach((cat) => {
			filters.push({ key: "peptideCategory", label: "Peptide Category", value: cat });
		});
		if (selectedResearchLevel !== "all") {
			filters.push({ key: "researchLevel", label: "Research Level", value: selectedResearchLevel });
		}
		if (selectedAvailability !== "all") {
			filters.push({ key: "availability", label: "Availability", value: selectedAvailability });
		}

		return filters;
	}, [
		selectedColors,
		selectedSizes,
		selectedPriceRange,
		resolvedCategories,
		searchQuery,
		selectedPeptideCategories,
		selectedResearchLevel,
		selectedAvailability,
	]);

	return {
		selectedCategories,
		selectedColors,
		selectedSizes,
		selectedPriceRange,
		searchQuery,
		selectedPeptideCategories,
		selectedResearchLevel,
		selectedAvailability,
		sortValue,
		activeFilters,
		handleCategoryToggle,
		handleColorToggle,
		handleSizeToggle,
		handlePriceRangeChange,
		handleSearchChange,
		handlePeptideCategoryToggle,
		handleResearchLevelChange,
		handleAvailabilityChange,
		handleSortChange,
		handleRemoveFilter,
		handleClearFilters,
	};
}
